import { errorMessage } from '@/lib/api';
import { useCanvasContext, type CanvasContractSummary } from '@/embed/context';
import { ActionCard } from '@/embed/ActionCard';
import { EmbedMessage } from '@/embed/EmbedMessage';
import type { EmbedWidgetProps } from '@/embed/types';

/** Human labels for the contract lifecycle statuses. */
const CONTRACT_STATUS_LABEL: Readonly<Record<string, string>> = {
  draft: 'Draft',
  client_review: 'In client review',
  changes_requested: 'Changes requested',
  approved: 'Approved',
  sent: 'Sent',
  viewed: 'Viewed',
  signed: 'Signed',
  declined: 'Declined',
  voided: 'Voided',
};

/** The label for a contract status, falling back to the raw value. */
function statusLabel(status: string): string {
  return CONTRACT_STATUS_LABEL[status] ?? status;
}

/**
 * The Opportunity **Overview** widget: a compact, at-a-glance panel. It shows
 * the bcecloud event's contract status and a proposal placeholder. The full
 * generate / edit / send workflow lives in the dedicated Contract tab
 * (`opportunity-contract`), which has the room the editor needs — so this stays
 * a status summary and does not link out of Salesforce.
 *
 * @param props - The Salesforce Opportunity record id.
 * @returns The Opportunity overview widget.
 */
export function OpportunityActions({ externalId }: EmbedWidgetProps): React.ReactElement {
  const ctx = useCanvasContext('Opportunity', externalId);

  if (ctx.isPending) return <EmbedMessage text="Loading this opportunity…" />;
  if (ctx.isError) {
    return <EmbedMessage tone="error" text={`Couldn't load this opportunity. ${errorMessage(ctx.error)}`} />;
  }

  const data = ctx.data;
  const event = data.kind === 'opportunity' ? data.event : null;
  const contracts: readonly CanvasContractSummary[] =
    data.kind === 'opportunity' ? data.contracts : [];
  const latest = contracts[0];

  return (
    <div className="space-y-3">
      <ActionCard
        title="Contract"
        status={latest ? statusLabel(latest.status) : event ? 'No contract yet' : 'No event'}
      >
        {!event ? (
          <p className="text-portal-small text-portal-muted">
            No bcecloud event exists for this opportunity yet. It is created when the opportunity is
            booked and synced.
          </p>
        ) : (
          <div className="space-y-1">
            <p className="text-portal-small text-portal-muted">
              {event.name ?? 'Event'}
              {event.accountName ? ` · ${event.accountName}` : ''}
              {contracts.length > 0
                ? ` · ${contracts.length} contract${contracts.length === 1 ? '' : 's'}`
                : ''}
            </p>
            <p className="text-portal-micro text-portal-muted">
              Generate, edit, and send in the <span className="font-bold">Contract</span> tab.
            </p>
          </div>
        )}
      </ActionCard>

      <ActionCard title="Proposal" description="Build a client proposal." status="Coming soon">
        <p className="text-portal-small text-portal-muted">
          The proposal generator will live here. It is not built yet.
        </p>
      </ActionCard>
    </div>
  );
}
